import { StorageLike } from "./storage.js";

/**
 * Human-facing app version shown in Settings → App data → App version.
 *
 * Bump APP_SEMVER on every commit (patch for fixes, minor for features, major for
 * breaking changes) and set APP_RELEASED_AT to that commit's UTC timestamp.
 */
export const APP_SEMVER = "1.3.13";
/** UTC instant this APP_SEMVER was released. Update this whenever you bump APP_SEMVER. */
export const APP_RELEASED_AT = "2026-10-24T13:58:01Z";

export const DEVICE_VERSION_KEY = "reading-bee:device-version";

export type DeviceVersion = {
  version: string;
  updatedAt: string;
};

const browserStorage = (): StorageLike | null => {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
};

export const readDeviceVersion = (storage: StorageLike | null = browserStorage()): DeviceVersion | null => {
  const raw = storage?.getItem(DEVICE_VERSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { version?: unknown; updatedAt?: unknown };
    if (typeof parsed.version === "string" && typeof parsed.updatedAt === "string") {
      return { version: parsed.version, updatedAt: parsed.updatedAt };
    }
  } catch {
    return null;
  }
  return null;
};

export const rememberDeviceVersion = (
  storage: StorageLike | null = browserStorage(),
  now = new Date(),
  version = APP_SEMVER,
): DeviceVersion => {
  const current = readDeviceVersion(storage);
  if (current?.version === version) return current;
  const next: DeviceVersion = { version, updatedAt: now.toISOString() };
  storage?.setItem(DEVICE_VERSION_KEY, JSON.stringify(next));
  return next;
};

export const formatAppTimestamp = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
};
